import { types, Instance, flow, getParent } from 'mobx-state-tree';
import api from '../services/api';

// Enums as MST enumerations
const TicketType = types.enumeration('TicketType', [
  'question',
  'feature_request', 
  'bug_report',
  'feedback'
]);

const TicketStatus = types.enumeration('TicketStatus', [
  'backlog',
  'in_progress',
  'resolved'
]);

const TicketPriority = types.enumeration('TicketPriority', [
  'low',
  'medium', 
  'high',
  'urgent'
]);

// MST Models
// User model - all fields optional except id to match backend nullable columns
// Using maybeNull (not maybe) because backend returns null, not undefined
const User = types.model('TicketUser', {
  id: types.string,
  name: types.maybeNull(types.string),
  email: types.maybeNull(types.string), // Made optional to match backend nullable email
});

const Comment = types.model('TicketComment', {
  id: types.string,
  nodeId: types.string,
  userId: types.string,
  userName: types.maybeNull(types.string),
  text: types.string,
  attachments: types.maybeNull(types.frozen()),
  createdAt: types.number,
  updatedAt: types.maybeNull(types.number),
  isEdited: types.boolean,
});

const Ticket = types.model('Ticket', {
  id: types.identifier,
  title: types.string,
  description: types.string,
  type: TicketType,
  status: TicketStatus,
  priority: TicketPriority,
  organizationId: types.string,
  creatorId: types.string,
  creator: types.maybeNull(User),
  assigneeId: types.maybeNull(types.string),
  assignee: types.maybeNull(User),
  tags: types.optional(types.array(types.string), []),
  resolvedAt: types.maybeNull(types.string),
  resolvedById: types.maybeNull(types.string),
  resolvedBy: types.maybeNull(User),
  createdAt: types.string,
  updatedAt: types.string,
  
  // Comments for this ticket
  comments: types.optional(types.array(Comment), []),
});

const TicketStatistics = types.model('TicketStatistics', {
  total: types.number,
  byStatus: types.model({
    backlog: types.number,
    inProgress: types.number,
    resolved: types.number,
  }),
  byType: types.model({
    question: types.number,
    featureRequest: types.number,
    bugReport: types.number,
    feedback: types.number,
  }),
  byPriority: types.model({
    urgent: types.number,
    high: types.number,
    medium: types.number,
    low: types.number,
  }),
});

/**
 * MST TICKET STORE
 * Manages all ticket-related state in MST tree
 */
export const TicketStoreMST = types
  .model('TicketStore', {
    // Data Collections
    tickets: types.optional(types.map(Ticket), {}),
    currentTicketId: types.maybe(types.string),
    statistics: types.maybe(TicketStatistics),
    
    // Loading States
    loading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    
    // UI State
    isCreateDialogOpen: types.optional(types.boolean, false),
    
    // Filters
    statusFilter: types.maybe(TicketStatus),
    typeFilter: types.maybe(TicketType),
    priorityFilter: types.maybe(TicketPriority),
    assigneeFilter: types.maybe(types.string),
  })
  .views(self => ({
    get currentTicket() {
      return self.currentTicketId ? self.tickets.get(self.currentTicketId) : null;
    },
    
    get ticketsArray() {
      return Array.from(self.tickets.values());
    },
    
    get filteredTickets() {
      return self.ticketsArray.filter(ticket => {
        if (self.statusFilter && ticket.status !== self.statusFilter) return false;
        if (self.typeFilter && ticket.type !== self.typeFilter) return false;
        if (self.priorityFilter && ticket.priority !== self.priorityFilter) return false;
        if (self.assigneeFilter && ticket.assigneeId !== self.assigneeFilter) return false;
        return true;
      });
    },
    
    get isAuthenticated() {
      const appStore = getParent(self) as any;
      return appStore.auth.isReallyAuthenticated;
    },
    
    get isAdmin() {
      const appStore = getParent(self) as any;
      // Check if current user is an admin or owner
      const userRole = appStore.auth.user?.role;
      return userRole === 'admin' || userRole === 'owner';
    },
    
    get axiosConfig() {
      // Use withCredentials instead of Authorization header
      return {  };
    }
  }))
  .actions(self => ({
    setCurrentTicket(ticketId: string | null) {
      self.currentTicketId = ticketId;
    },
    
    setLoading(loading: boolean) {
      self.loading = loading;
    },
    
    setError(error: string | null) {
      self.error = error;
    },
    
    setCreateDialogOpen(open: boolean) {
      self.isCreateDialogOpen = open;
    },
    
    setStatusFilter(status: typeof TicketStatus.Type | null) {
      self.statusFilter = status;
    },
    
    setTypeFilter(type: typeof TicketType.Type | null) {
      self.typeFilter = type;
    },
    
    setPriorityFilter(priority: typeof TicketPriority.Type | null) {
      self.priorityFilter = priority;
    },
    
    setAssigneeFilter(assigneeId: string | null) {
      self.assigneeFilter = assigneeId;
    },
    
    // Load all tickets
    fetchTickets: flow(function* () {
      if (!self.isAuthenticated) {
        console.log('[TicketStore] Not authenticated, skipping tickets load');
        return;
      }
      
      self.setLoading(true);
      self.setError(null);
      
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        const response = yield api.get(`${backendUrl}/api/tickets`, 
          self.axiosConfig
        );
        
        // Clear existing tickets
        self.tickets.clear();
        
        // Add new tickets
        response.data.forEach((ticket: any) => {
          self.tickets.put({
            ...ticket,
            comments: [], // Comments loaded separately
          });
        });
        
        console.log(`✅ [TicketStore] Loaded ${response.data.length} tickets`);
        
      } catch (error) {
        console.error('❌ [TicketStore] Failed to fetch tickets:', error);
        self.setError('Failed to load tickets');
      } finally {
        self.setLoading(false);
      }
    }),
    
    // Load statistics
    fetchStatistics: flow(function* () {
      if (!self.isAuthenticated) return;
      
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        const response = yield api.get(`${backendUrl}/api/tickets/statistics`, 
          self.axiosConfig
        );
        
        self.statistics = response.data;
        console.log('✅ [TicketStore] Loaded statistics');
        
      } catch (error) {
        console.error('❌ [TicketStore] Failed to fetch statistics:', error);
      }
    }),
    
    // Create new ticket
    createTicket: flow(function* (ticketData: any) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      self.setLoading(true);
      
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        const response = yield api.post(`${backendUrl}/api/tickets`, ticketData, 
          self.axiosConfig
        );
        
        // Add the new ticket to the store
        self.tickets.put({
          ...response.data,
          comments: [],
        });
        
        console.log('✅ [TicketStore] Created ticket:', response.data.id);
        return response.data;
        
      } catch (error) {
        console.error('❌ [TicketStore] Failed to create ticket:', error);
        self.setError('Failed to create ticket');
        throw error;
      } finally {
        self.setLoading(false);
      }
    }),
    
    // Update ticket status (admin only)
    updateTicketStatus: flow(function* (ticketId: string, status: typeof TicketStatus.Type) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        const response = yield api.patch(
          `${backendUrl}/api/tickets/${ticketId}/status`,
          { status },
          self.axiosConfig
        );
        
        // Update the ticket in the store
        const updatedTicket = response.data;
        self.tickets.put({
          ...updatedTicket,
          comments: self.tickets.get(ticketId)?.comments || [],
        });
        
        console.log('✅ [TicketStore] Updated ticket status:', ticketId);
        return true;
        
      } catch (error) {
        console.error('❌ [TicketStore] Failed to update ticket status:', error);
        self.setError('Failed to update ticket status');
        return false;
      }
    }),
    
    // Delete ticket (admin only)
    deleteTicket: flow(function* (ticketId: string) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        yield api.delete(
          `${backendUrl}/api/tickets/${ticketId}`,
          self.axiosConfig
        );
        
        // Remove from store
        self.tickets.delete(ticketId);
        
        console.log('✅ [TicketStore] Deleted ticket:', ticketId);
        return true;
        
      } catch (error) {
        console.error('❌ [TicketStore] Failed to delete ticket:', error);
        self.setError('Failed to delete ticket');
        return false;
      }
    }),
    
    // Reset store
    reset() {
      self.tickets.clear();
      self.currentTicketId = null;
      self.statistics = null;
      self.loading = false;
      self.error = null;
      self.isCreateDialogOpen = false;
      self.statusFilter = null;
      self.typeFilter = null;
      self.priorityFilter = null;
      self.assigneeFilter = null;
      
      console.log('🔄 [TicketStore] Reset complete');
    },
  }));

export type ITicketStoreMST = Instance<typeof TicketStoreMST>;
